import { OrderStatus } from '../types/OrderStatus'
import { InvoiceFlags } from '../constants'
import { OrderDto, OrderLineDto, OrderSummaryDto } from '../types/swaggerTypes'

/**
 * Checks if an order is eligible for invoice download based on its status and invoice flag
 * @param order The order to check (can be either OrderDto or OrderSummaryDto)
 * @returns boolean indicating if the order is eligible for invoice download
 */
export const isOrderEligibleForInvoice = (
  order?: OrderDto | OrderSummaryDto
): boolean => {
  if (!order?.orderStatus) {
    return false
  }

  // For OrderSummaryDto, we need to check the invoiceFlag from the 'any' type
  // For OrderDto, we can check it directly
  const invoiceFlag =
    'invoiceFlag' in order ? order.invoiceFlag : (order as any).invoiceFlag

  if (invoiceFlag !== InvoiceFlags.Y.toString()) {
    return false
  }

  const eligibleStatuses = [
    OrderStatus.Shipped,
    OrderStatus.Delivered,
    OrderStatus.InTransit,
    OrderStatus.OutForDelivery,
    OrderStatus.FailedAttempt,
    OrderStatus.Failed_Attempt,
    OrderStatus.AvailableForPickup
  ]

  return eligibleStatuses.some(
    (status) => order.orderStatus === status.toString()
  )
}

/**
 * The line that carries an order's patient, physician and programme: the
 * medication line, else the first line with a patient number or programme,
 * else the first line. Order lines come in no guaranteed order (Data Platform),
 * so lines[0] can be an ancillary line such as a data logger with null fields.
 */
export const getPrimaryOrderLine = (
  order: OrderDto | undefined | null
): OrderLineDto | undefined => {
  const lines = order?.lines ?? []
  return (
    lines.find((line) => line.medicationFlag === 'Y') ??
    lines.find((line) => !!line.patientNumber || line.programId != null) ??
    lines[0]
  )
}
